import { useState } from "react";
import { advisoryApi, type I18n, type Json } from "../advisoryApi";
import { i18n, isRecord } from "../format";
import { Modal } from "../ui";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface AssessmentOption {
  id: string;
  productTypeId: string;
  productTypeNameI18n: I18n;
  mode: "QUIZ" | "QUESTIONNAIRE" | string;
  minScore: number | null;
  questionsToAsk: number | null;
  maxAttempts: number | null;
  attemptResetDays: number | null;
}
interface Experience {
  productTypeId: string;
  productTypeName?: string;
  hasExperience: boolean;
  verdict: string | null;
  assessments?: { mode: string; score: number | null; minScore: number | null; passed: boolean | null; status?: string }[];
}
interface KeData {
  options: AssessmentOption[];
  current: { inputData: Json | null; calculatedData: { experiences?: Experience[] } | null } | null;
  previous: Json | null;
  typesInUse: unknown[] | null;
}
interface QuizQuestion {
  id: string;
  labelI18n: I18n;
  options: { id: string; labelI18n: I18n }[];
}

function typeIdsInUse(v: unknown[] | null | undefined): Set<string> {
  const ids = (v ?? []).map((x) => (typeof x === "string" ? x : isRecord(x) ? (x.productTypeId ?? x.id) : null));
  return new Set(ids.filter((x): x is string => typeof x === "string"));
}

/**
 * Knowledge & Experience (`product_assessment`) — QP's product-assessment
 * widget. Only the product types the portfolios actually hold need assessing,
 * so with a model portfolio the step is already complete and the quiz stays
 * out of the way. When QP opens it (a custom portfolio holds a product type),
 * each type gets its quiz: one question at a time, then Suitable / Not suitable.
 */
export function KnowledgeStep({ sessionId, steps, readOnly, onSaved }: StepProps) {
  const load = useStepLoad<KeData>(sessionId, "product-assessment", {});
  const saver = useSaver(onSaved);
  const [quizFor, setQuizFor] = useState<AssessmentOption | null>(null);

  if (load.loading && !load.data) return <p className="muted">Loading knowledge & experience…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;

  const isOpen = steps.some((s) => s.status === "open");
  const inUse = typeIdsInUse(load.data?.typesInUse);
  const experiences = load.data?.current?.calculatedData?.experiences ?? [];
  const all = load.data?.options ?? [];
  // types-in-use unreadable but QP says open → show every assessed type.
  const options = inUse.size > 0 ? all.filter((o) => inUse.has(o.productTypeId)) : isOpen ? all : [];

  async function reusePrevious() {
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "product-assessment", {}, { action: "reuse_previous" }));
    if (r) load.reload();
  }

  if (!isOpen && options.length === 0) {
    return (
      <section className="adv-step-card">
        <ul className="check-list">
          <li className="check-row check-pass">
            <span className="check-icon">✓</span>
            <span>
              <strong>Nothing to assess for this advice.</strong> Knowledge & experience is only asked for the product types the
              client is being advised into. A model portfolio needs none; it opens here if a custom portfolio holds, for example,
              UCITS funds.
            </span>
          </li>
        </ul>
      </section>
    );
  }

  return (
    <section className="adv-step-card">
      {load.data?.previous && !readOnly && (
        <div className="adv-prefill-note">
          <span>The client has answered this before. You can reuse the previous answers.</span>
          <button type="button" className="btn btn-ghost adv-btn" onClick={reusePrevious} disabled={saver.saving}>
            Use previous answers
          </button>
        </div>
      )}
      {saver.error && <div className="banner-error">{saver.error}</div>}
      <ul className="check-list">
        {options.map((o) => {
          const exp = experiences.find((e) => e.productTypeId === o.productTypeId);
          const outcome = exp?.assessments?.find((a) => a.mode === o.mode);
          const passed = outcome?.passed === true || exp?.hasExperience === true;
          const failed = outcome?.passed === false;
          return (
            <li key={o.id} className={passed ? "check-row check-pass" : failed ? "check-row check-warn" : "check-row adv-check-open"}>
              <span className="check-icon">{passed ? "✓" : failed ? "!" : ""}</span>
              <span className="adv-ke-body">
                <strong>{i18n(o.productTypeNameI18n)}</strong>
                <span className="muted">
                  {passed
                    ? `Suitable${outcome?.score != null ? ` · ${outcome.score} of ${o.questionsToAsk ?? "?"} correct` : ""}`
                    : failed
                      ? `Not suitable${outcome?.score != null ? ` · ${outcome.score} correct, ${o.minScore} needed` : ""}`
                      : `Quiz · ${o.questionsToAsk ?? "?"} questions, ${o.minScore ?? "?"} correct to pass, ${o.maxAttempts ?? "?"} attempts`}
                </span>
              </span>
              {!readOnly && !passed && o.mode === "QUIZ" && (
                <button type="button" className="btn btn-ghost adv-btn" onClick={() => setQuizFor(o)}>
                  {failed ? "Try again" : "Start quiz"}
                </button>
              )}
            </li>
          );
        })}
        {options.length === 0 && (
          <li className="check-row adv-check-open">
            <span className="check-icon" />
            <span>Waiting for the portfolio's product types.</span>
          </li>
        )}
      </ul>
      {quizFor && (
        <QuizModal
          sessionId={sessionId}
          option={quizFor}
          onSaved={onSaved}
          onClose={() => {
            setQuizFor(null);
            load.reload();
          }}
        />
      )}
    </section>
  );
}

function QuizModal({
  sessionId,
  option,
  onSaved,
  onClose,
}: {
  sessionId: number;
  option: AssessmentOption;
  onSaved: StepProps["onSaved"];
  onClose: () => void;
}) {
  const saver = useSaver(onSaved);
  const [questions, setQuestions] = useState<QuizQuestion[] | null>(null);
  const [attempt, setAttempt] = useState<number | null>(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ passed: boolean; remaining: number | null } | null>(null);
  const name = i18n(option.productTypeNameI18n);

  async function start() {
    const r = await saver.run(() =>
      advisoryApi.saveStep(sessionId, "product-assessment", {}, { action: "quiz_start", productTypeId: option.productTypeId }),
    );
    if (!r) return;
    const qs = Array.isArray(r.result.questions) ? (r.result.questions as QuizQuestion[]) : [];
    setQuestions(qs);
    setAttempt(typeof r.result.attemptNumber === "number" ? r.result.attemptNumber : null);
  }

  async function finish() {
    if (!questions) return;
    const r = await saver.run(() =>
      advisoryApi.saveStep(sessionId, "product-assessment", {}, {
        action: "quiz_submit",
        productTypeId: option.productTypeId,
        answers: questions.map((q) => ({ questionId: q.id, selectedOptionId: answers[q.id] ?? "" })),
      }),
    );
    if (!r) return;
    const avail = isRecord(r.result.availability) ? r.result.availability : null;
    setResult({ passed: r.result.passed === true, remaining: typeof avail?.remaining === "number" ? avail.remaining : null });
  }

  const q = questions?.[idx];
  return (
    <Modal title={`Knowledge quiz · ${name}`} onClose={onClose} wide>
      {saver.error && <div className="banner-error">{saver.error}</div>}
      {result ? (
        <>
          <ul className="check-list">
            <li className={result.passed ? "check-row check-pass" : "check-row check-warn"}>
              <span className="check-icon">{result.passed ? "✓" : "!"}</span>
              <span>
                <strong>{result.passed ? "Suitable" : "Not suitable"}</strong>
                {!result.passed && result.remaining !== null && (
                  <span className="muted"> · {result.remaining > 0 ? `${result.remaining} attempt${result.remaining === 1 ? "" : "s"} left` : "no attempts left for now"}</span>
                )}
              </span>
            </li>
          </ul>
          <div className="modal-actions">
            <button type="button" className="btn btn-primary adv-btn" onClick={onClose}>
              Done
            </button>
          </div>
        </>
      ) : !questions ? (
        <>
          <p>
            Ask the client {option.questionsToAsk ?? "a few"} questions about {name}. {option.minScore ?? "Most"} correct answers are
            needed; there are {option.maxAttempts ?? "limited"} attempts per {option.attemptResetDays ?? "few"} days.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost adv-btn" onClick={onClose}>
              Not now
            </button>
            <button type="button" className="btn btn-primary adv-btn" onClick={start} disabled={saver.saving}>
              {saver.saving ? "Starting…" : "Start the quiz"}
            </button>
          </div>
        </>
      ) : q ? (
        <>
          <span className="muted">
            Question {idx + 1} of {questions.length}
            {attempt ? ` · attempt ${attempt}` : ""}
          </span>
          <fieldset className="adv-question">
            <legend>{i18n(q.labelI18n)}</legend>
            <div className="option-list">
              {q.options.map((o) => (
                <label key={o.id} className={answers[q.id] === o.id ? "option-row selected" : "option-row"}>
                  <input
                    type="radio"
                    name={`quiz-${q.id}`}
                    checked={answers[q.id] === o.id}
                    onChange={() => setAnswers((a) => ({ ...a, [q.id]: o.id }))}
                  />
                  {i18n(o.labelI18n)}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost adv-btn" onClick={() => setIdx((i) => i - 1)} disabled={idx === 0}>
              Previous
            </button>
            {idx < questions.length - 1 ? (
              <button type="button" className="btn btn-primary adv-btn" onClick={() => setIdx((i) => i + 1)} disabled={!answers[q.id]}>
                Next
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary adv-btn"
                onClick={finish}
                disabled={saver.saving || questions.some((x) => !answers[x.id])}
              >
                {saver.saving ? "Grading…" : "Finish"}
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="muted">No questions were dealt for this attempt.</p>
      )}
    </Modal>
  );
}
